// REALISTIC ZOMBIE (2026-10-23, owner directive: "really realistic models",
// zombie look explicitly unfrozen — the PLAYER avatar look stays frozen, see
// CLAUDE.md invariant 5). A rigged, skinned human (Microsoft Rocketbox
// Male_Adult_01, MIT) repainted as a zombie, driven by six retargeted CC0
// clips (see public/models/zombie/CREDITS.md and agent/zombie-asset/).
//
// Avatar owns the lifecycle: while an avatar is a zombie AND this module is
// armed, it hides its procedural body and drives one ZombieModel instead.
//
// Contract:
// - ONE skinned mesh, two draw calls (skin+head atlas, alpha-tested hair) per
//   zombie; materials are SHARED (one pair per tint colour, module-cached,
//   never disposed) so a 30-zombie purge wave is 60 draws, not 30x the
//   procedural body's ~15 parts.
// - Never fades: the materials are tagged `npNoFade` so Avatar's opacity /
//   ghost passes skip them. A transparent skinned variant would be a fresh
//   shader program compiled mid-fight; the corpse is removed by the server
//   1.6 s after death anyway, before any fade would read.
// - Armed ONLY by warmZombieModel() under the match load screen, after one
//   real frame through the game's render path has compiled its programs
//   (colour + skinned shadow depth) and uploaded its textures. If the GLB is
//   not loaded by then, the round keeps the procedural zombie — it never pops
//   in mid-fight.
// - No per-frame allocation (invariant 6): update() writes weights/timeScales
//   on preallocated actions and calls mixer.update once.
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { instantiate, loadGltf } from "./gltfAssets.js";

export const ZOMBIE_GLB_URL = "/models/zombie/zombie.glb";

/** The GLB's idle pose crowns at 1.808 m (hair cards included; measured in a
 *  real browser with Box3.setFromObject(precise) across every clip frame —
 *  agent/shot-zombie.mjs `measure`). Scaled so that crown lands at 1.70 m, the
 *  top of the server's 1.7 m hitbox: the visible head then spans ~1.43–1.70 m,
 *  inside the headshot band (top 25% = 1.275–1.70 m), like the procedural
 *  head (top 1.705). The model faces +Z; avatars face -Z, hence the π yaw. */
export const ZOMBIE_MODEL_SCALE = 1.70 / 1.808;

/** Clip order == action index. */
const CLIPS = ["idle", "walk", "run", "attack", "death", "hit"] as const;
const IDLE = 0, WALK = 1, RUN = 2, ATTACK = 3, DEATH = 4, HIT = 5;
const N_LOCO = 5; // idle..death are mutually exclusive; HIT is an overlay
/** Ground speed each in-place loop was authored for (m/s, after the scale) —
 *  measured from the planted foot's travel in the retargeted clips. */
const WALK_NOMINAL = 0.26;
const RUN_NOMINAL = 1.7;
/** Locomotion bands with hysteresis (m/s). Zombies move at 2.3–5.1 m/s
 *  (MOVE.walkSpeed 5.7 x zombie.speed 0.4–0.9), so RUN carries almost all
 *  movement; the slow lurch WALK covers stopping, turning and the stab slow. */
const WALK_ON = 0.35, WALK_OFF = 0.2, RUN_ON = 1.1, RUN_OFF = 0.85;
/** The server deletes a dead zombie 1.6 s after the kill — the 3.3 s death
 *  take is played fast enough to reach the ground inside that window. */
const DEATH_RATE = 1.75;
const HIT_LEN = 0.55;
const FAR_STEP = 1 / 15; // far-LOD mixer cadence

let template: GLTF | null = null;
let armed = false;
/** The renderer the warm frame ran on. Every match builds its own
 *  GameRenderer (= a fresh WebGL context with an empty program cache), so
 *  arming is per renderer: a new match re-warms before it re-arms. */
let armedFor: object | null = null;
let skinMat: THREE.MeshStandardMaterial | null = null;
let hairMat: THREE.MeshStandardMaterial | null = null;
const tintCache = new Map<string, [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial]>();
const _tint = new THREE.Color();

/** True once warmZombieModel() has compiled + uploaded everything. */
export function zombieModelArmed(): boolean { return armed && template !== null; }

/** A match that will NOT warm (weak device / non-zombie mode) must not inherit
 *  the previous match's arming: that was a different WebGL context, so the
 *  skinned programs would cold-compile mid-fight here. */
export function disarmZombieModel(): void { armed = false; armedFor = null; }

function prepare(g: GLTF | null): GLTF | null {
  if (!g || template) return template ?? g;
  g.scene.updateMatrixWorld(true);
  g.scene.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    // Frustum culling for a skinned mesh tests `boundingSphere` in mesh space;
    // left null, three would compute it on first cull by skinning every
    // vertex (per instance). Compute it ONCE on the template at bind pose and
    // pad it for the reach of the attack and the death fall (~1.3 m of root
    // travel). Clones copy it.
    m.computeBoundingSphere();
    if (m.boundingSphere) m.boundingSphere.radius += 1.4;
    m.castShadow = true;
    m.receiveShadow = true;
    const mat = m.material as THREE.MeshStandardMaterial;
    mat.userData.npNoFade = true;
    if (mat.name === "zombie_hair") hairMat = mat; else skinMat = mat;
  });
  if (!skinMat || !hairMat) { console.error("[zombie] unexpected GLB layout"); return null; }
  template = g;
  return g;
}

/** Start the fetch early (match start) so the load screen only has to wait
 *  for whatever is left. Safe to call repeatedly. */
export function preloadZombieModel(): void {
  void loadGltf(ZOMBIE_GLB_URL).then(prepare);
}

/** Shared material pair for a server bodyColor: the variants (rot shades,
 *  brute, runner, ghoul…) keep a subtle cast of their colour on the realistic
 *  body. Colour is a uniform — every tint hits the same compiled program. */
function materialsFor(tint: string): [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial] {
  const key = tint || "-";
  let pair = tintCache.get(key);
  if (!pair) {
    const s = skinMat!.clone(), h = hairMat!.clone();
    if (tint) {
      _tint.set(tint);
      const mx = Math.max(_tint.r, _tint.g, _tint.b, 1e-3);
      _tint.setRGB(_tint.r / mx, _tint.g / mx, _tint.b / mx);
      s.color.setRGB(1, 1, 1).lerp(_tint, 0.22);
      h.color.setRGB(1, 1, 1).lerp(_tint, 0.12);
    }
    for (const m of [s, h]) { m.userData.shared = true; m.userData.npNoFade = true; }
    pair = [s, h];
    tintCache.set(key, pair);
  }
  return pair;
}

export class ZombieModel {
  /** Container in avatar-group space: yaw π + scale. */
  readonly root = new THREE.Group();
  private readonly inner: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly acts: THREE.AnimationAction[] = [];
  private readonly w = new Float32Array(N_LOCO);
  private readonly skinned: THREE.SkinnedMesh[] = [];
  private loco = IDLE;
  private hitT = -1;
  private dying = false;
  private farAcc = 0;
  /** Per-instance rate jitter so a horde doesn't step in lockstep. */
  private readonly jitter = 0.92 + Math.random() * 0.16;

  constructor(tint = "") {
    const g = template!;
    this.inner = instantiate(g);
    this.root.add(this.inner);
    this.root.rotation.y = Math.PI;
    this.root.scale.setScalar(ZOMBIE_MODEL_SCALE);
    this.root.name = "npZombieModel";
    const [s, h] = materialsFor(tint);
    this.inner.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isSkinnedMesh) return;
      m.material = (m.material as THREE.Material).name === "zombie_hair" ? h : s;
      this.skinned.push(m);
    });
    this.mixer = new THREE.AnimationMixer(this.inner);
    for (const name of CLIPS) {
      const clip = THREE.AnimationClip.findByName(g.animations, name);
      const a = this.mixer.clipAction(clip);
      if (name === "death") { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; a.timeScale = DEATH_RATE; }
      a.play();
      a.setEffectiveWeight(0);
      // desync: every zombie starts its loops at a different phase
      if (name !== "death" && name !== "hit") a.time = Math.random() * clip.duration;
      this.acts.push(a);
    }
    this.reset();
  }

  /** Back to a standing idle (spawn / revive). */
  reset(): void {
    this.dying = false;
    this.hitT = -1;
    this.loco = IDLE;
    this.w.fill(0);
    this.w[IDLE] = 1;
    this.acts[DEATH].reset();
    this.acts[DEATH].timeScale = DEATH_RATE;
    for (let i = 0; i < this.acts.length; i++) this.acts[i].setEffectiveWeight(i === IDLE ? 1 : 0);
    this.mixer.update(0);
  }

  startDeath(): void {
    if (this.dying) return;
    this.dying = true;
    this.hitT = -1;
    const d = this.acts[DEATH];
    d.reset();
    d.timeScale = DEATH_RATE;
  }

  /** Flinch overlay (a bullet landed). Ignored while dying. */
  hit(): void {
    if (this.dying || (this.hitT >= 0 && this.hitT < HIT_LEN * 0.6)) return;
    this.hitT = 0;
    this.acts[HIT].time = 0.08;
  }

  /** Once per rendered frame. `speed` = horizontal m/s. */
  update(dt: number, speed: number, attacking: boolean, far: boolean): void {
    if (far) {
      this.farAcc += dt;
      if (this.farAcc < FAR_STEP) return;
      dt = this.farAcc;
    }
    this.farAcc = 0;
    // ---- pick the locomotion layer (hysteretic speed bands)
    if (this.dying) this.loco = DEATH;
    else if (attacking) this.loco = ATTACK;
    else {
      const run = this.loco === RUN ? speed > RUN_OFF : speed > RUN_ON;
      const walk = this.loco === WALK || this.loco === RUN ? speed > WALK_OFF : speed > WALK_ON;
      this.loco = run ? RUN : walk ? WALK : IDLE;
    }
    const k = 1 - Math.exp(-dt * (this.dying ? 14 : 8));
    for (let i = 0; i < N_LOCO; i++) this.w[i] += ((i === this.loco ? 1 : 0) - this.w[i]) * k;
    let h = 0;
    if (this.hitT >= 0) {
      this.hitT += dt;
      if (this.hitT < HIT_LEN) h = 0.65 * Math.sin(Math.PI * this.hitT / HIT_LEN);
      else this.hitT = -1;
    }
    for (let i = 0; i < N_LOCO; i++) this.acts[i].setEffectiveWeight(this.w[i] * (1 - h));
    this.acts[HIT].setEffectiveWeight(h);
    // in-place loops: match the cadence to the real ground speed (some foot
    // slide at the extremes is the accepted cost of clamping)
    this.acts[WALK].timeScale = Math.min(2.4, Math.max(0.7, speed / WALK_NOMINAL)) * this.jitter;
    this.acts[RUN].timeScale = Math.min(2.3, Math.max(0.85, speed / RUN_NOMINAL)) * this.jitter;
    this.acts[ATTACK].timeScale = 1.15 * this.jitter;
    this.mixer.update(dt);
  }

  skinnedMeshes(): readonly THREE.SkinnedMesh[] { return this.skinned; }

  /** Free the per-instance pieces only: the skeleton's bone texture and the
   *  mixer's bindings. Geometry, textures and the tint materials are shared. */
  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.inner);
    for (const m of this.skinned) m.skeleton.dispose();
    this.root.removeFromParent();
  }
}

/** The minimal surface of GameRenderer this needs (kept structural so this
 *  module doesn't pull the renderer class in as a type cycle). */
interface WarmTarget { readonly scene: THREE.Scene; readonly camera: THREE.Camera; render(): void }

/**
 * Under the match load screen: wait (bounded) for the GLB, then put ONE
 * zombie in front of the camera and render ONE real frame through the game's
 * own path (post chain included) — that compiles the skinned colour program,
 * the skinned shadow-depth program (renderer.compile() never builds shadow
 * programs), and uploads the textures + bone texture. Then arm the module.
 * Idempotent; returns whether realistic zombies are live this round.
 * The CALLER gates weak devices (match.ts: lowSpec(), which includes the TV
 * app) — never calling this keeps the procedural zombie. (No renderer.js
 * import here: the esbuild avatar benches stub that module.)
 */
export async function warmZombieModel(r: WarmTarget, timeoutMs = 3000): Promise<boolean> {
  if (armed && armedFor === r) return true;
  armed = false; // a new context: nothing is compiled there yet
  armedFor = null;
  const g = await Promise.race([
    loadGltf(ZOMBIE_GLB_URL).then(prepare),
    new Promise<null>((res) => window.setTimeout(() => res(null), timeoutMs)),
  ]);
  if (!g || !template) { console.log("[zombie] model not ready at the load-screen deadline — procedural zombies this round"); return false; }
  const z = new ZombieModel("");
  const cam = r.camera;
  const fwd = new THREE.Vector3();
  cam.getWorldDirection(fwd);
  z.root.position.copy(cam.position).addScaledVector(fwd, 3);
  z.root.position.y -= 1.2;
  for (const m of z.skinnedMeshes()) m.frustumCulled = false; // also bypasses the shadow-camera cull
  r.scene.add(z.root);
  z.update(1 / 60, 0, false, false);
  try { r.render(); } catch { /* best-effort, like precompile */ }
  z.dispose();
  armed = true;
  armedFor = r;
  return true;
}
